import { isObject } from 'underscore';
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import updatePlainTextEmails from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/emails/helpers/use-plain-text-email';
import getEmailToSend from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/emails/scheduled-emails/get-email-to-send';
import { Channel } from '@/types/channel.types';
import { EmailTypeDisplayNames, IScheduledEmail } from '@/types/email.types';
import { BrandliveEvent } from '@/types/projects.types';
import { SessionTypesEnum } from '@/types/session.types';
import { LanguagesAbbr } from '@/types/translation.types';
import { editScheduledEmail } from '..';
import toastTrigger from '@ui/toast/toast-trigger';

type TUpdateScheduledEmailsParams = {
  workingProject: BrandliveEvent;
  currentChannel: Channel;
  publishedUrl: string;
  scheduledEmails: IScheduledEmail[] | null;
  language: LanguagesAbbr;
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
};

const updateScheduledEmails = async ({
  scheduledEmails,
  workingProject,
  currentChannel,
  publishedUrl,
  language,
  dispatch,
}: TUpdateScheduledEmailsParams) => {
  // any unsent scheduled emails will need to be updated to account for any relevant event/session content changes
  // and to save changes made to the emails in the editor
  try {
    if (scheduledEmails) {
      const unsentEmails = scheduledEmails.filter((email) => !email.sent);
      await Promise.all(
        unsentEmails.map(async (email) => {
          // event emails are sent in a specific language but session emails go out in all event languages
          const emailLanguage = email.email_type.includes('session') ? undefined : (email.language ?? language);
          let customizations = email.customizations ?? {};
          if (!email?.sent && email.working_customizations) {
            // don't override customizations if email is sent, it will only confuse the user
            customizations = email.working_customizations;
          }

          const updatedEmailContent = await getEmailToSend({
            emailType: email.email_type,
            workingProject,
            currentChannel,
            publishedUrl,
            emailContent: customizations,
            language: emailLanguage,
            sessionUUID: email.session_uuid,
            emailSendTime: email.delivery_date,
            timezone: email.timezone,
            emailSettings: email.settings,
          });

          if (updatedEmailContent) {
            const updatedEmail: IScheduledEmail = {
              ...email,
              customizations: updatedEmailContent,
              working_customizations: null,
            };

            // update plain text emails if needed
            const updatedCustomizations = await updatePlainTextEmails({
              workingProject,
              usePlainTextEmail: !!updatedEmail.settings?.usePlainTextEmail,
              sessionUuid: email.session_uuid,
              channel: currentChannel,
              publishedUrl,
              language,
              customizations: updatedEmail.customizations,
              emailType: email.email_type,
              deliveryDate: email.delivery_date,
              showStartTime: !!updatedEmail.settings?.showStartTime,
              showAddCalendar: !!updatedEmail.settings?.showAddCalendar,
              isAllOnDemand: workingProject.sessions.every(
                (session) => session.session_type === SessionTypesEnum.onDemand
              ),
              isOnDemandSession:
                workingProject.sessions?.find((s) => s.uuid === email.session_uuid)?.session_type ===
                SessionTypesEnum.onDemand,
            });

            updatedEmail.customizations = updatedCustomizations;

            dispatch(editScheduledEmail({ email: updatedEmail }));

            const emailTypeDisplayName = EmailTypeDisplayNames[email.email_type] ?? email.email_type;
            if (!isObject(updatedEmailContent) || !Object.keys(updatedEmailContent).length) {
              console.error('No email content found for email: ', email);
              toastTrigger(
                `
								<div class="S2">
									<p>No scheduled email content was found.</p>
									<p>Email type: ${emailTypeDisplayName.toUpperCase()}</p>
									<p>Delivery date: ${getDateTime(email)}</p>
									<p>Please refresh your page, edit your email content and re-publish.</p>
									<p>If the issue persists, delete and re-create the email.</p>
								</div>
								`,
                {
                  type: 'error',
                  autoClose: 1000000,
                }
              );
            } else {
              const languages = Array.from(new Set(workingProject.sessions.flatMap((session) => session.languages)));
              // if missing a language from languages is missing in emailContent
              const missingLangs = languages.filter((lang) => !Object.keys(updatedEmailContent).includes(lang));
              if (missingLangs.length) {
                console.error('Missing email content for languages: ', missingLangs, email);
                toastTrigger(
                  `
									<div class="S2">
									<p>Email missing content for the following languages: ${missingLangs.join(', ')}</p>
									<p>Email type: ${emailTypeDisplayName.toUpperCase()}</p>
									<p>Delivery date: ${getDateTime(email)}</p>
									<p>Please refresh your page, edit your email content and re-publish.</p>
									<p>If the issue persists, delete and re-create the email.</p>
								</div>
									`,
                  {
                    type: 'error',
                    autoClose: 1000000,
                  }
                );
              }
            }
          }
        })
      );
    }
  } catch (e) {
    console.error('error updating scheduled emails: ', e);
  }
};

export default updateScheduledEmails;

function getDateTime(email: IScheduledEmail) {
  const scheduledEmailDate = new Date(email.delivery_date);
  const date = scheduledEmailDate.toLocaleString(navigator.language, {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });
  const time = scheduledEmailDate.toLocaleTimeString(navigator.language, {
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
  });
  return `${date} ${time}`;
}
